import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { SetupIntent, StripeError } from '@stripe/stripe-js';
import { Button, Card } from 'antd';

import SubscriptionPaidFeatures from 'components/Payment/SubscriptionPaidFeatures/SubscriptionPaidFeatures';

import { selectClientStripeId, selectUser } from 'features/auth/authSelectors';
import { selectIsDefaultPaymentMethodSet } from 'features/client/clientSelectors';

import useErrorCatch from 'hooks/useErrorCatch';

import { get, put } from 'utils/api/api';
import { notify } from 'utils/toast/createToast';

import { CLIENT_SECRET, PAYMENT_METHODS } from 'constants/endpoints';
import { AlertType } from 'constants/enums';

import { ReactComponent as AddPlusIcon } from 'assets/icons/addPlus.svg';
import { ReactComponent as BankCardIcon } from 'assets/icons/bankCard.svg';

import styles from './SetupIntentPayment.module.scss';
import { getSetupIntentPaymentErrorDescription } from './utils/getSetupIntentPaymentErrorDescription';

interface SetupIntentPaymentProps {
  onSuccess?: () => void;
  onError?: (error: StripeError) => void;
  onSubmitStart?: () => void;
  loading?: boolean;
}

export const SetupIntentPayment: React.FC<SetupIntentPaymentProps> = ({
  onSuccess,
  onError,
  onSubmitStart,
  loading: externalLoading
}) => {
  const { t } = useTranslation();

  const [internalLoading, setInternalLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState<string>('');
  const [setupIntentLoading, setSetupIntentLoading] = useState(true);
  const [stripeReady, setStripeReady] = useState(false);

  const stripe = useStripe();
  const elements = useElements();
  const catchError = useErrorCatch();
  const stripeId = useSelector(selectClientStripeId);
  const user = useSelector(selectUser);
  const isDefaultPaymentMethodSet = useSelector(
    selectIsDefaultPaymentMethodSet
  );

  const loading = externalLoading ?? internalLoading;

  useEffect(() => {
    if (stripe && elements) {
      setStripeReady(true);
    }
  }, [stripe, elements]);

  useEffect(() => {
    const createSetupIntent = async () => {
      if (!stripeId || !stripeReady) {
        setSetupIntentLoading(false);
        return;
      }

      try {
        const response = await get(CLIENT_SECRET);
        setClientSecret(response.code);
      } catch (error) {
        console.error('🔍 SetupIntentPayment: Setup intent error', error);
        catchError({
          error,
          default: {
            message: t('error.message.errorCreatingCardForm')
          }
        });
      } finally {
        setSetupIntentLoading(false);
      }
    };

    createSetupIntent();
  }, [stripeId, stripeReady, catchError]);

  const notifyBackend = async (paymentMethodId: string): Promise<void> => {
    try {
      await put(`${PAYMENT_METHODS}/${paymentMethodId}`, {});
      onSuccess?.();
    } catch (apiError) {
      console.error('Error notifying the backend:', apiError);
      notify({
        type: AlertType.Warning,
        message: t('error.message.cardAddedWithError')
      });
    }
  };

  const handleSuccessfulSetup = async (
    setupIntent: SetupIntent
  ): Promise<void> => {
    const paymentMethodId = setupIntent.payment_method as string;
    await notifyBackend(paymentMethodId);
    elements?.getElement(CardElement)?.clear();
  };

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();

    if (!stripe || !elements || !clientSecret) {
      console.error('Stripe not ready');
      return;
    }

    setInternalLoading(true);
    onSubmitStart?.();

    try {
      const { error, setupIntent } = await stripe.confirmCardSetup(
        clientSecret,
        {
          payment_method: {
            card: elements.getElement(CardElement)!,
            billing_details: {
              name: user?.name,
              email: user?.email
            }
          }
        }
      );

      if (error) {
        throw error;
      }

      if (setupIntent?.status === 'succeeded') {
        await handleSuccessfulSetup(setupIntent);
      } else {
        throw new Error(t('error.message.setupIntentWasNotSuccessful'));
      }
    } catch (err) {
      const error = err as StripeError;
      const errorDescription = getSetupIntentPaymentErrorDescription(error, t);

      catchError({
        error,
        default: {
          message: t('error.message.addingCard'),
          description: errorDescription
        }
      });
      onError?.(error);
    } finally {
      setInternalLoading(false);
    }
  };

  if (setupIntentLoading) {
    return (
      <Card className={styles.paymentCard}>
        <div>{t('payment.setupIntentPayment.loadingCard')}</div>
      </Card>
    );
  }

  if (!stripeReady || !clientSecret) {
    return (
      <Card className={styles.paymentCard}>
        <div>{t('payment.setupIntentPayment.loadingPaymentSystem')}</div>
      </Card>
    );
  }

  return (
    <>
      {!isDefaultPaymentMethodSet && <SubscriptionPaidFeatures />}
      <Card className={styles.paymentCard}>
        <div className={styles.paymentHeader}>
          <div className={styles.paymentIcon}>
            <BankCardIcon />
          </div>
          <div className={styles.paymentText}>
            <h3 className={styles.paymentTitle}>
              {t('payment.setupIntentPayment.title')}
            </h3>
            <p className={styles.paymentDescription}>
              {t('payment.setupIntentPayment.description')}
            </p>
          </div>
        </div>
        <form onSubmit={handleSubmit}>
          <div className={styles.cardDetails}>
            <div className={styles.cardInputLabel}>
              {t('payment.setupIntentPayment.cardDetails')}
            </div>
            <CardElement
              options={{
                hidePostalCode: true
              }}
            />
          </div>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            disabled={!stripeReady}
            className={styles.addButton}
          >
            <span className={styles.buttonIcon}>
              <AddPlusIcon />
            </span>
            {t('payment.setupIntentPayment.addPaymentMethod')}
          </Button>
        </form>
      </Card>
    </>
  );
};
